'use client';

import { FC, ReactNode, useEffect, useState } from 'react';
import {
  ImageField,
  isFilled,
  LinkField,
  LinkToMediaField,
  RichTextField,
} from '@prismicio/client';
import { PrismicRichText } from '@prismicio/react';
import { PrismicNextImage } from '@prismicio/next';
import { OutlineCtaLink } from '@/components/common/OutlineCtaLink';
import { hasAnchor } from '@/utils/anchor-link-utils';
import { usePrefersReducedMotion } from '@/hooks';

/**
 * Overlay used when `applyBottomGradient` is true: fades a dark + green wash into
 * the bottom third of the hero so the title stays legible over any image while
 * carrying the brand green tone. Matches the previous BlogHero overlay.
 */
const BOTTOM_GRADIENT_OVERLAY =
  'linear-gradient(180deg, rgba(0,0,0,0) 30%, rgba(0,0,0,0.6) 100%), linear-gradient(180deg, rgba(5,242,115,0) 30%, rgba(5,242,115,0.35) 100%)';

/**
 * Default brand background when no image / video / custom className is provided:
 * a diagonal green wash going from a lighter top-left to a darker bottom-right,
 * approximated from designer color-meter samples (originally captured in Display
 * P3, mapped to sRGB hex here).
 */
const DEFAULT_BACKGROUND_GRADIENT =
  'linear-gradient(135deg, #5AC46B 0%, #50AF6B 20%, #367B5D 45%, #235545 75%, #1E4C42 100%)';

const TITLE_CLASS =
  'w-full break-words text-left font-inter text-[32px] font-medium leading-[1.2] text-white md:text-[55px]';

const EYEBROW_CLASS =
  'w-full text-left text-[12px] font-normal uppercase leading-[1.2] text-white md:text-[14px]';

export interface PageHeroRedesignProps {
  /** Small uppercase label above the title. Accepts a plain string or Prismic rich text. */
  eyebrow?: RichTextField | string | null;
  logo?: ImageField;
  /** Extra classes merged onto the logo image — use to override the default
   * `h-auto w-auto max-w-full` sizing (e.g. `scale-[1.15] origin-top-left` to
   * enlarge without touching the shell's base rules). */
  logoClassName?: string;
  title?: RichTextField;
  description?: RichTextField;
  ctaLink?: LinkField;
  /** Fallback anchor when `ctaLink` is empty. */
  ctaAnchor?: string | null;
  /** Overrides the label pulled from `ctaLink.text` if provided. */
  ctaLabel?: string;
  backgroundImage?: ImageField;
  backgroundVideo?: LinkToMediaField;
  backgroundVideoPoster?: ImageField;
  /** When true, layers the dark + green bottom gradient (blog-style). */
  applyBottomGradient?: boolean;
  /** Custom overlay CSS `background-image` value. Only honored when
   * `applyBottomGradient` is true — otherwise ignored so consumers can't
   * accidentally paint over a non-gradient hero. */
  bottomGradientOverlay?: string;
  /** Optional slot rendered after the hero card, inside the same section. */
  children?: ReactNode;
  /**
   * Escape hatch to override the section's base background. When omitted, the
   * brand green diagonal gradient (see `DEFAULT_BACKGROUND_GRADIENT`) is used.
   */
  backgroundClassName?: string;
  imageLayout?: 'cover' | 'fill';
}

const PageHeroRedesign: FC<PageHeroRedesignProps> = ({
  eyebrow,
  logo,
  logoClassName,
  title,
  description,
  ctaLink,
  ctaAnchor,
  ctaLabel,
  backgroundImage,
  backgroundVideo,
  backgroundVideoPoster,
  applyBottomGradient = false,
  bottomGradientOverlay,
  children,
  backgroundClassName,
  imageLayout = 'cover',
}) => {
  const bottomOverlayImage = bottomGradientOverlay ?? BOTTOM_GRADIENT_OVERLAY;
  const hasLogo = isFilled.image(logo);
  const hasVideo = isFilled.linkToMedia(backgroundVideo);
  const hasImage = isFilled.image(backgroundImage);
  const hasBackgroundMedia = hasVideo || hasImage;

  const prefersReducedMotion = usePrefersReducedMotion();
  const [shouldMountVideo, setShouldMountVideo] = useState(false);

  useEffect(() => {
    if (typeof window === 'undefined') {
      return;
    }
    const schedule =
      'requestIdleCallback' in window
        ? window.requestIdleCallback
        : (cb: IdleRequestCallback) =>
            setTimeout(() => cb({ didTimeout: false, timeRemaining: () => 0 }));
    const handle = schedule(() => setShouldMountVideo(true));
    return () => {
      if ('cancelIdleCallback' in window && typeof handle === 'number') {
        window.cancelIdleCallback(handle);
      }
    };
  }, []);

  const eyebrowIsString = typeof eyebrow === 'string';
  const eyebrowIsRichText =
    !eyebrowIsString && Array.isArray(eyebrow) && isFilled.richText(eyebrow);
  const hasEyebrow =
    (eyebrowIsString && (eyebrow as string).trim().length > 0) ||
    eyebrowIsRichText;

  const hasCta = isFilled.link(ctaLink) || hasAnchor(ctaAnchor);

  const useDefaultGradient = !backgroundClassName;

  return (
    <section
      className={`relative overflow-hidden ${backgroundClassName ?? ''}`}
      style={
        useDefaultGradient
          ? { background: DEFAULT_BACKGROUND_GRADIENT }
          : undefined
      }
    >
      {hasBackgroundMedia && (
        <div className="absolute inset-0 overflow-hidden">
          {hasVideo ? (
            <>
              {isFilled.image(backgroundVideoPoster) && (
                <PrismicNextImage
                  field={backgroundVideoPoster}
                  priority
                  fill
                  sizes="100vw"
                  className="pointer-events-none absolute inset-0 h-full w-full object-cover object-center"
                  fallbackAlt=""
                />
              )}
              {shouldMountVideo && !prefersReducedMotion && (
                <video
                  autoPlay
                  loop
                  muted
                  playsInline
                  preload="none"
                  poster={backgroundVideoPoster?.url ?? undefined}
                  className="pointer-events-none absolute inset-0 h-full w-full object-cover object-center"
                >
                  <source src={backgroundVideo!.url} type="video/webm" />
                  <source src={backgroundVideo!.url} type="video/mp4" />
                </video>
              )}
            </>
          ) : (
            hasImage && (
              <PrismicNextImage
                field={backgroundImage}
                priority
                fill
                sizes="100vw"
                className={`pointer-events-none absolute inset-0 h-full w-full object-center ${
                  imageLayout === 'fill' ? 'object-fill' : 'object-cover'
                }`}
                fallbackAlt=""
              />
            )
          )}
          {applyBottomGradient ? (
            <div
              className="absolute inset-0"
              style={{ backgroundImage: bottomOverlayImage }}
            />
          ) : (
            <div className="absolute inset-0 bg-black/15" />
          )}
        </div>
      )}

      {/* When there's no image/video, still apply the bottom gradient wash so the
          hero doesn't sit on a flat black; keeps consistent styling with images. */}
      {!hasBackgroundMedia && applyBottomGradient && (
        <div
          className="pointer-events-none absolute inset-0"
          style={{ backgroundImage: bottomOverlayImage }}
        />
      )}

      <div className="relative flex min-h-[480px] flex-col justify-end px-5 pb-10 pt-[168px] md:min-h-[560px] md:px-[30px] md:pb-12 lg:min-h-[707px] lg:pt-[135px] lg:pb-[46px] landscape-short:pt-24 landscape-short:pb-6">
        <div className="mx-auto flex w-full max-w-[1280px] flex-col items-start">
          <div className="flex flex-col items-start gap-4 py-6 md:py-10">
            <div className="flex w-full flex-col items-start gap-0">
              {hasEyebrow &&
                (eyebrowIsRichText ? (
                  <PrismicRichText
                    field={eyebrow as RichTextField}
                    components={{
                      paragraph: ({ children: c }) => (
                        <p className={EYEBROW_CLASS}>{c}</p>
                      ),
                      em: ({ children: c }) => <em className="italic">{c}</em>,
                    }}
                  />
                ) : (
                  <p className={EYEBROW_CLASS}>{eyebrow as string}</p>
                ))}

              {hasLogo && (
                <PrismicNextImage
                  field={logo}
                  className={`h-auto w-auto max-w-full ${
                    hasEyebrow ? 'mt-5' : ''
                  } ${logoClassName ?? ''}`}
                  fallbackAlt=""
                />
              )}

              {isFilled.richText(title) && (
                <PrismicRichText
                  field={title}
                  components={{
                    heading1: ({ children: c }) => (
                      <h1
                        className={`${TITLE_CLASS} ${
                          hasLogo || hasEyebrow ? 'mt-2' : ''
                        }`}
                      >
                        {c}
                      </h1>
                    ),
                    paragraph: ({ children: c }) => (
                      <h1
                        className={`${TITLE_CLASS} ${
                          hasLogo || hasEyebrow ? 'mt-5' : ''
                        }`}
                      >
                        {c}
                      </h1>
                    ),
                    strong: ({ children: c }) => (
                      <strong className="font-medium">{c}</strong>
                    ),
                    em: ({ children: c }) => <em className="italic">{c}</em>,
                  }}
                />
              )}
            </div>

            {isFilled.richText(description) && (
              <div className="w-full break-words text-left">
                <PrismicRichText
                  field={description}
                  components={{
                    paragraph: ({ children: c }) => (
                      <p className="w-full text-[16px] font-light leading-[1.5] text-white md:text-[18px]">
                        {c}
                      </p>
                    ),
                    strong: ({ children: c }) => (
                      <strong className="font-semibold">{c}</strong>
                    ),
                    hyperlink: ({ children: c, node }) => (
                      <a
                        href={node.data.url ?? '#'}
                        className="text-[#00f557] underline"
                        target="_blank"
                        rel="noopener noreferrer"
                      >
                        {c}
                      </a>
                    ),
                  }}
                />
              </div>
            )}

            {hasCta && (
              <div className="pt-2">
                <OutlineCtaLink
                  field={ctaLink ?? ({} as LinkField)}
                  anchor={ctaAnchor ?? null}
                  label={ctaLabel}
                  tone="dark"
                  className="whitespace-nowrap"
                />
              </div>
            )}
          </div>
        </div>
      </div>

      {children}
    </section>
  );
};

export default PageHeroRedesign;
